import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/ui/data-table';
import { Modal } from '@/components/ui/modal';
import { ActionButton } from '@/components/ui/action-button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/lib/supabase';
import type { Grade, SchoolLevel } from '@/types';

interface FormData {
  name: string;
  school_level_id: string;
}

export default function Grades() {
  const [grades, setGrades] = useState<Grade[]>([]);
  const [schoolLevels, setSchoolLevels] = useState<SchoolLevel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState<Grade | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    school_level_id: '',
  });

  useEffect(() => {
    fetchGrades();
    fetchSchoolLevels();
  }, []);

  const fetchGrades = async () => {
    try {
      const { data, error } = await supabase
        .from('sms_grades')
        .select(`
          *,
          school_level:sms_school_levels(*)
        `)
        .order('name');

      if (error) throw error;
      setGrades(data);
    } catch (error) {
      console.error('Error fetching grades:', error);
      toast.error('Failed to fetch grades');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSchoolLevels = async () => {
    const { data, error } = await supabase
      .from('sms_school_levels')
      .select('*')
      .order('name');
    
    if (error) {
      console.error('Error fetching school levels:', error);
      return;
    }
    setSchoolLevels(data);
  };

  const handleSubmit = async () => {
    try {
      if (selectedGrade) {
        // Update existing grade
        const { error } = await supabase
          .from('sms_grades')
          .update(formData)
          .eq('id', selectedGrade.id);

        if (error) throw error;
        toast.success('Grade updated successfully');
      } else {
        // Create new grade
        const { error } = await supabase
          .from('sms_grades')
          .insert(formData);

        if (error) throw error;
        toast.success('Grade created successfully');
      }

      setModalOpen(false);
      fetchGrades();
    } catch (error) {
      console.error('Error saving grade:', error);
      toast.error('Failed to save grade');
    }
  };

  const handleDelete = async () => {
    if (!selectedGrade) return;

    try {
      const { error } = await supabase
        .from('sms_grades')
        .delete()
        .eq('id', selectedGrade.id);

      if (error) throw error;

      toast.success('Grade deleted successfully');
      setDeleteDialogOpen(false);
      fetchGrades();
    } catch (error) {
      console.error('Error deleting grade:', error);
      toast.error('Failed to delete grade');
    }
  };

  const columns = [
    { header: 'Name', accessorKey: 'name' },
    {
      header: 'School Level',
      accessorKey: 'school_level',
      cell: ({ row }: any) => row.original.school_level?.name,
    },
    {
      header: 'Actions',
      accessorKey: 'actions',
      cell: ({ row }: any) => (
        <div className="flex gap-2">
          <ActionButton
            icon={Pencil}
            variant="edit"
            onClick={() => {
              setSelectedGrade(row.original);
              setFormData({
                name: row.original.name,
                school_level_id: row.original.school_level_id,
              });
              setModalOpen(true);
            }}
          />
          <ActionButton
            icon={Trash2}
            variant="delete"
            onClick={() => {
              setSelectedGrade(row.original);
              setDeleteDialogOpen(true);
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="container mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Grades</h1>
        <Button
          onClick={() => {
            setSelectedGrade(null);
            setFormData({
              name: '',
              school_level_id: '',
            });
            setModalOpen(true);
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Grade
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={grades}
        isLoading={isLoading}
      />

      <Modal
        title={selectedGrade ? 'Edit Grade' : 'Add Grade'}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleSubmit}
      >
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Name</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">School Level</label>
            <Select
              value={formData.school_level_id}
              onValueChange={(value) => setFormData({ ...formData, school_level_id: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a school level" />
              </SelectTrigger>
              <SelectContent>
                {schoolLevels.map((level) => (
                  <SelectItem key={level.id} value={level.id}>
                    {level.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Modal>

      <Modal
        title="Delete Grade"
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        confirmText="Delete"
        confirmVariant="destructive"
      >
        <p>Are you sure you want to delete this grade? This action cannot be undone.</p>
      </Modal>
    </div>
  );
}